import { FC, ReactElement, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import { DialogMenu, SelectBox } from "@/components";
import { filter_black_icon } from "@/constants";
import { tokens } from "@/tokens";

export const initialFilters = {
  filerCode: "",
  importerNumber: "",
};

export type Filters = typeof initialFilters;

interface IAdvanceFilter {
  appliedFilters: Filters;
  onFiltersChange: (filters: Filters) => void;
  onDraftFiltersChange?: (filters: Filters) => void;
  filterOptions: {
    brokerOptions: string[];
    iorOptions: string[];
  };
  isLoadingOptions?: boolean;
  onClose: () => void;
  anchorEl: Element | null;
}

const AdvanceFilter: FC<IAdvanceFilter> = ({
  appliedFilters,
  onFiltersChange,
  onDraftFiltersChange,
  filterOptions,
  isLoadingOptions = false,
  onClose,
  anchorEl,
}): ReactElement => {
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [areFilterApplying, setAreFilterApplying] = useState<boolean>(false);

  useEffect(() => {
    setFilters({ ...appliedFilters });
    onDraftFiltersChange?.({ ...appliedFilters });
  }, [appliedFilters, anchorEl]);

  const applyFilters = async (): Promise<void> => {
    setAreFilterApplying(true);
    await onFiltersChange(filters);
    setAreFilterApplying(false);
    onClose();
  };

  const resetFilters = (): void => {
    setFilters(initialFilters);
    onDraftFiltersChange?.(initialFilters);
  };

  const handleChangeValue =
    (name: keyof Filters) =>
    (value: string): void => {
      const nextFilters = {
        ...filters,
        [name]: value,
      };
      setFilters(nextFilters);
      onDraftFiltersChange?.(nextFilters);
    };

  const toSelectOptions = (values: string[]) =>
    values.map((value) => ({ label: value, value }));

  return (
    <DialogMenu
      id="advance-filter-menu"
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={2.5}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <img src={filter_black_icon} alt="filter" />
          <Typography
            variant="h5"
            fontSize={18}
            fontWeight={tokens.fontWeight.bold}
            color={tokens.text.primary}
          >
            Advance Filter
          </Typography>
          <Box
            sx={{
              width: 24,
              height: 24,
              display: "grid",
              placeContent: "center",
              backgroundColor: tokens.status.danger,
              color: "white",
              borderRadius: "50%",
            }}
          >
            <Typography
              variant="caption"
              fontWeight={tokens.fontWeight.semibold}
              color="white"
            >
              {Object.values(filters).filter(Boolean).length}
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            color: tokens.brand.primary,
            textDecorationLine: "underline",
            cursor: "pointer",
          }}
          variant="body2"
          fontWeight={tokens.fontWeight.semibold}
          onClick={resetFilters}
        >
          Clear All
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ p: 2 }} spacing={1}>
        <SelectBox
          label="Broker (Filer Code)"
          placeholder={
            isLoadingOptions ? "Loading brokers..." : "Select Broker"
          }
          value={filters.filerCode}
          handleChangeValue={handleChangeValue("filerCode")}
          options={toSelectOptions(filterOptions.brokerOptions)}
        />
        <SelectBox
          label="IOR"
          placeholder={isLoadingOptions ? "Loading IORs..." : "Select IOR"}
          value={filters.importerNumber}
          handleChangeValue={handleChangeValue("importerNumber")}
          options={toSelectOptions(filterOptions.iorOptions)}
        />
        <Typography variant="caption" color={tokens.text.secondary}>
          Broker and IOR options affect each other based on the current
          selection.
        </Typography>
      </Stack>
      <Divider />
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        p={2}
        sx={{
          background: tokens.bg.hover,
        }}
      >
        <Button variant="white-outlined" sx={{ flex: 1 }} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ flex: 1, gap: 1 }}
          onClick={applyFilters}
          disabled={areFilterApplying}
          startIcon={areFilterApplying ? <CircularProgress size={20} /> : null}
        >
          {areFilterApplying ? "Applying" : "Apply"}
        </Button>
      </Stack>
    </DialogMenu>
  );
};

export default AdvanceFilter;
